import { createContext, useCallback, useContext, useMemo, useRef, useState } from "react";

const ToastCtx = createContext(null);

/**
 * One small dark pill near the foot of the screen, gone by itself in a couple
 * of seconds. It exists for outcomes that happen off-screen or between screens
 * — signing in, an order quietly attached to a fresh account — where nothing
 * in place changes to say "it worked". Feedback that already lives next to the
 * action (a qty stepper, a Saved button, a confirmation screen) stays there;
 * this is not a second voice for those.
 */
export const ToastProvider = ({ children }) => {
  const [message, setMessage] = useState(null);
  const timerRef = useRef(null);

  const toast = useCallback((text) => {
    setMessage(text);
    if (timerRef.current) clearTimeout(timerRef.current);
    timerRef.current = setTimeout(() => setMessage(null), 2500);
  }, []);

  const value = useMemo(() => ({ toast }), [toast]);

  return (
    <ToastCtx.Provider value={value}>
      {children}
      {message && (
        <div
          data-testid="toast"
          role="status"
          aria-live="polite"
          className="fixed inset-x-0 bottom-24 z-[95] flex justify-center px-5 pointer-events-none"
        >
          <div className="toast-in max-w-md bg-neutral-900 text-white text-sm font-bold px-4 py-2.5 rounded-full shadow-lg">
            {message}
          </div>
        </div>
      )}
    </ToastCtx.Provider>
  );
};

export const useToast = () => {
  const ctx = useContext(ToastCtx);
  if (!ctx) throw new Error("useToast must be inside ToastProvider");
  return ctx;
};
